import React, { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Label } from "../../../../shadcn/ui/label";
import { Checkbox } from "../../../../shadcn/ui/checkbox";
import { Button } from "../../../../shadcn/ui/button";
import { Package, Tag, AlertTriangle, Layers, Clock, CheckCircle2 } from "lucide-react";
import { addonService } from "../../../../services/addonService";
import { useUserCurrency } from "../../../../hooks/useUserCurrency";
import { isAddonDurationCompatible } from "../../../../utils/addonPricingUtils";
import logger from "../../../../utils/logger";

const SpaceAddonsCard = ({ control, errors, isReadOnly = false, categoryId, spaceDurationType = "HOURLY" }) => {
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const userCurrencyCode = useUserCurrency();

  useEffect(() => {
    fetchAddons();
  }, [categoryId]);

  const fetchAddons = async () => {
    try {
      setLoadingAddons(true);
      const params = { size: 100 };
      if (categoryId) {
        params.categoryId = categoryId;
      }
      const response = await addonService.getMyOrganizationAddons(params);

      if (response.status === 200 && response.data) {
        const addonsData = response.data.content || response.data.data?.content || [];
        setAddons(addonsData);
      }
    } catch (error) {
      logger.error("Error fetching addons:", error);
      setAddons([]);
    } finally {
      setLoadingAddons(false);
    }
  };

  const formatPrice = (price, duration) => {
    const durationText = {
      HOURLY: "per hour",
      DAILY: "per day",
      WEEKLY: "per week",
      MONTHLY: "per month",
    };
    const currency = userCurrencyCode || "KES";
    return `${currency} ${Number(price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${durationText[duration] || ""}`;
  };

  const formatAddonInfo = addon => {
    const categoryName = addon.serviceCategory?.name || "";
    const categoryText = categoryName ? ` • ${categoryName}` : "";
    const instanceText = addon.numberOfInstances > 1 ? ` • ${addon.numberOfInstances} available` : "";
    return `${categoryText}${instanceText}`.replace(/^•\s/, "");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg flex items-center">
              <Package className="w-4 h-4 mr-2" />
              Space Add-ons
            </CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-1">
              Optional services and equipment compatible with this space ({spaceDurationType.toLowerCase()} rate)
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loadingAddons ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            <Controller
              name="addons"
              control={control}
              render={({ field }) => (
                <>
                  {!categoryId ? (
                    <div className="text-center py-6 border border-gray-200 border-dashed rounded-lg">
                      <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-1">Select a category first</p>
                      <p className="text-xs text-gray-400">Add-ons are specific to each space category</p>
                    </div>
                  ) : (
                    <>
                      {addons.map(addon => {
                        const isChecked = field.value.includes(addon.id);
                        const { isCompatible, reason } = isAddonDurationCompatible(addon.duration, spaceDurationType);
                        const isDisabled = isReadOnly || (!isCompatible && !isChecked);

                        return (
                          <div
                            key={addon.id}
                            className={`p-3 border rounded-lg transition-all ${
                              !isCompatible
                                ? "bg-slate-50/70 border-slate-200 opacity-75"
                                : isChecked
                                ? "bg-blue-50/40 border-blue-200"
                                : "hover:bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                id={`addon-${addon.id}`}
                                checked={isChecked}
                                disabled={isDisabled}
                                onCheckedChange={checked => {
                                  if (!isCompatible && checked) return;
                                  const newValue = checked
                                    ? [...field.value, addon.id]
                                    : field.value.filter(id => id !== addon.id);
                                  field.onChange(newValue);
                                }}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                                      <Label
                                        htmlFor={`addon-${addon.id}`}
                                        className={`text-sm font-medium block ${
                                          isDisabled && !isChecked ? "cursor-not-allowed text-gray-500" : "cursor-pointer text-gray-900"
                                        }`}
                                      >
                                        {addon.name}
                                      </Label>
                                      {addon.duration && (
                                        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                          {addon.duration}
                                        </span>
                                      )}
                                      {typeof addon.numberOfInstances === "number" && addon.numberOfInstances > 0 && (
                                        <span className="text-[10px] text-slate-600 flex items-center gap-0.5">
                                          <Layers className="w-2.5 h-2.5" />
                                          {addon.numberOfInstances} unit{addon.numberOfInstances > 1 ? "s" : ""}
                                        </span>
                                      )}
                                    </div>
                                    {addon.description && (
                                      <p className="text-xs text-gray-600 line-clamp-1">{addon.description}</p>
                                    )}
                                  </div>
                                  <div className="ml-3 text-right shrink-0">
                                    <div className="flex items-center text-blue-600 font-medium text-sm">
                                      <Tag className="w-3 h-3 mr-1" />
                                      <span>{formatPrice(addon.unitPrice, addon.duration)}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mt-1">
                                  <div className="text-xs text-gray-500">{formatAddonInfo(addon)}</div>
                                  <div className="flex items-center space-x-1 text-xs">
                                    {addon.type && (
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                          addon.type === "SUBSCRIPTION"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-700"
                                        }`}
                                      >
                                        {addon.type === "SUBSCRIPTION" ? "Subscription" : "One-time"}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Incompatibility Warning */}
                                {!isCompatible && (
                                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Duration mismatch:</strong> {reason || `Cannot attach ${addon.duration} add-on to a ${spaceDurationType} space.`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {addons.length === 0 && !loadingAddons && (
                        <div className="text-center py-6 border border-gray-200 border-dashed rounded-lg">
                          <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 mb-1">No add-ons available for this category</p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            />
          </div>
        )}

        {errors.addons && <p className="text-sm text-red-500 mt-2">{errors.addons.message}</p>}
      </CardContent>
    </Card>
  );
};

export default SpaceAddonsCard;
